import { apiClient } from "../../../shared/api/AllApi";
import { isNetworkError } from "../../../shared/lib/connectivity";
import { idbDelete, idbGet, idbGetAll, idbPut } from "../../../shared/lib/posIdb";

const STORE = "saleQueue";

/** Keep offline sales for at least 5 hours; 24h gives a safe buffer overnight. */
export const OFFLINE_SALE_TTL_MS = 24 * 60 * 60 * 1000;

export const OFFLINE_QUEUE_CHANGED_EVENT = "velan:pos-offline-queue-changed";

function notifyQueueChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(OFFLINE_QUEUE_CHANGED_EVENT));
  }
}

function newClientSaleId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `offline-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @typedef {object} OfflineSaleReceipt
 * @property {unknown[]} items
 * @property {number} total
 * @property {number} [percent]
 * @property {number} [retained]
 * @property {boolean} [taxed]
 * @property {string} [paymentMethod]
 * @property {string|null} [customerLabel]
 * @property {string|Date} [date]
 */

/**
 * @typedef {object} OfflineSaleRecord
 * @property {string} clientSaleId
 * @property {object} payload - body for POST /pos/sale
 * @property {OfflineSaleReceipt} receipt
 * @property {number} createdAt
 * @property {number} expiresAt
 * @property {"pending"|"syncing"|"failed"} status
 * @property {string|null} [lastError]
 * @property {number} [attempts]
 */

/**
 * Enqueue a sale to sync when the network returns.
 * @param {{ payload: object, receipt: OfflineSaleReceipt }} args
 * @returns {Promise<OfflineSaleRecord>}
 */
export async function enqueueOfflineSale({ payload, receipt }) {
  const now = Date.now();
  const clientSaleId = newClientSaleId();
  /** @type {OfflineSaleRecord} */
  const record = {
    clientSaleId,
    payload: {
      ...payload,
      client_sale_id: clientSaleId,
    },
    receipt: {
      ...receipt,
      date: receipt?.date instanceof Date ? receipt.date.toISOString() : receipt?.date || new Date(now).toISOString(),
    },
    createdAt: now,
    expiresAt: now + OFFLINE_SALE_TTL_MS,
    status: "pending",
    lastError: null,
    attempts: 0,
  };

  await idbPut(STORE, record);
  notifyQueueChanged();
  return record;
}

/**
 * @returns {Promise<OfflineSaleRecord[]>}
 */
export async function listOfflineSales() {
  try {
    const all = await idbGetAll(STORE);
    const now = Date.now();
    const living = [];
    for (const row of all) {
      if (row.expiresAt && row.expiresAt < now) {
        await idbDelete(STORE, row.clientSaleId).catch(() => {});
        continue;
      }
      living.push(row);
    }
    living.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    return living;
  } catch {
    return [];
  }
}

export async function countPendingOfflineSales() {
  const list = await listOfflineSales();
  return list.filter((r) => r.status !== "failed" || isRetryableFailed(r)).length;
}

function isRetryableFailed(record) {
  // Keep failed network attempts; drop only if we ever mark non-retryable.
  return record?.lastError !== "validation";
}

/**
 * Map queue rows into sale-list shaped objects for the Orders subview.
 */
export function offlineSaleToListItem(record) {
  const created = new Date(record.createdAt || Date.now()).toISOString();
  return {
    id: `pending-${record.clientSaleId}`,
    client_sale_id: record.clientSaleId,
    _pendingSync: true,
    document_no: `OFFLINE-${String(record.clientSaleId).slice(0, 8).toUpperCase()}`,
    created_at: created,
    total_price: record.receipt?.total ?? 0,
    commission_amount: record.receipt?.retained ?? 0,
    is_taxed: record.receipt?.taxed !== false,
    payment_method: record.receipt?.paymentMethod || record.payload?.payment_method || "cash",
    status: "pending_sync",
    customer: record.receipt?.customerLabel
      ? { name: record.receipt.customerLabel }
      : null,
    receipt: record.receipt,
    payload: record.payload,
  };
}

/**
 * Build a printable receipt snapshot from a queue record.
 */
export function offlineSaleToPrintPayload(record) {
  const date = record.receipt?.date ? new Date(record.receipt.date) : new Date(record.createdAt);
  return {
    id: null,
    documentNo: `OFFLINE-${String(record.clientSaleId).slice(0, 8).toUpperCase()}`,
    items: record.receipt?.items || [],
    total: record.receipt?.total ?? 0,
    percent: record.receipt?.percent ?? 0,
    retained: record.receipt?.retained ?? 0,
    taxed: record.receipt?.taxed !== false,
    paymentMethod: record.receipt?.paymentMethod || "cash",
    customerLabel: record.receipt?.customerLabel || null,
    date,
    _pendingSync: true,
  };
}

async function markSyncing(clientSaleId) {
  const row = await idbGet(STORE, clientSaleId);
  if (!row) return null;
  const next = {
    ...row,
    status: "syncing",
    attempts: (row.attempts || 0) + 1,
  };
  await idbPut(STORE, next);
  return next;
}

async function markFailed(clientSaleId, reason) {
  const row = await idbGet(STORE, clientSaleId);
  if (!row) return;
  await idbPut(STORE, {
    ...row,
    status: "failed",
    lastError: reason || "failed",
  });
  notifyQueueChanged();
}

async function markPending(clientSaleId, reason) {
  const row = await idbGet(STORE, clientSaleId);
  if (!row) return;
  await idbPut(STORE, {
    ...row,
    status: "pending",
    lastError: reason || null,
  });
  notifyQueueChanged();
}

async function removeSynced(clientSaleId) {
  await idbDelete(STORE, clientSaleId);
  notifyQueueChanged();
}

/** @type {Promise<{ synced: number, failed: number, remaining: number }>|null} */
let syncInFlight = null;

/**
 * Drain the offline sale queue FIFO while online.
 */
export async function syncPendingOfflineSales() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { synced: 0, failed: 0, remaining: (await listOfflineSales()).length };
  }

  if (syncInFlight) return syncInFlight;

  syncInFlight = (async () => {
    let synced = 0;
    let failed = 0;
    const pending = await listOfflineSales();
    const toSync = pending.filter(
      (r) => r.status === "pending" || (r.status === "failed" && isRetryableFailed(r)) || r.status === "syncing"
    );

    for (const row of toSync) {
      if (typeof navigator !== "undefined" && navigator.onLine === false) break;

      const current = await markSyncing(row.clientSaleId);
      if (!current) continue;

      try {
        // Body-only idempotency key — avoid custom headers (they trigger CORS preflight).
        await apiClient.post("/pos/sale", {
          ...current.payload,
          client_sale_id: current.clientSaleId,
        });
        await removeSynced(current.clientSaleId);
        synced += 1;
      } catch (err) {
        if (isNetworkError(err)) {
          await markPending(current.clientSaleId, "network");
          break;
        }
        const status = err?.response?.status;
        if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
          // Validation / auth / business rule — don't loop forever
          await markFailed(current.clientSaleId, "validation");
          failed += 1;
        } else {
          await markPending(current.clientSaleId, err?.message || "server");
          failed += 1;
        }
      }
    }

    const remaining = (await listOfflineSales()).length;
    return { synced, failed, remaining };
  })().finally(() => {
    syncInFlight = null;
  });

  return syncInFlight;
}
